import * as React from "react";
import { DlcoResults, StudyRelevantParameters } from "../Shared/PFTAPITypes";
import { SessionMetadata, FixedCommentMutation, TestAcceptabilityMutation, OverreadStatusEnum, ModelError, Permission, OverreadChallengeStatusEnum, OverreadStatusIdEnum } from "../../misc/api/apiTypes";
import { DlcoResultsTable } from "./DlcoResultsTable";
import { Navigate } from "react-router";
import { useFixedComments, ToggleSessionFixedCommentsVisibilityAction } from "../Shared/UseFixedComments";
import FileAttachmentsTable from "../../file-attachments/components/viewer/FileAttachmentsTable";
import { FileAttachmentsTableMode } from "../../file-attachments/models";
import NotesPanel from "../Shared/NotesPanel";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import SessionMetadataTable from "../Shared/SessionMetadataTable";
import AcceptabilityDashboard from "../Shared/AcceptabilityDashboard";
import { useAcceptability, LoadingBestValuesAction, UpdateBestTestAction, UpdateRepeatabilityAction, DoneLoadingBestValuesAction } from '../Shared/UseAcceptability';
import { DlcoAnalysisSetTypeEnum } from "../../misc/api/ApiDlcoAnalysisSetType";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { SessionTypes } from "../../misc/api/ApiSessionTypes";
import { DlcoAnalysisSet, DlcoSession, SessionNote } from "webportalclient";
import { ChallengeAlert } from "../Shared/ChallengeAlert";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { useSecurityContext } from "../../Core/SecurityContext";
import { useNavigate, useParams } from "react-router-dom";
import { GoBackToList } from "../Shared/AnalysisHelpers";
import { LoadAnalysisSetSelectionsAction, useNextGenFixedComments } from "../Spirometry/Testing/UseNextGenFixedComments";
import { FixedCommentSetTypeEnum } from "../../misc/api/FixedComments/FixedCommentSetTypes";
import { Authorization } from "../../misc/components/Authorization";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { ModalSize } from '../../misc/components/modal';

enum ViewState {
    View = "view",
    Overread = "overread"
}

interface ExpectedMatchParams {
    analysisSetId: string;
    viewState: ViewState;
}

export const DlcoAnalysisScreen = () => {
    const { analysisSetId, viewState } = useParams();
    const navigate = useNavigate();
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();
    const [sessionMetadata, setSessionMetadata] = React.useState<SessionMetadata>(null);
    const [analysisSet, setAnalysisSet] = React.useState<DlcoResults>(null);
    const [hbParameterDisplay, setHbParameterDisplay] = React.useState<boolean>(null);
    const [overreadAnalysisSet, setOverreadAnalysisSet] = React.useState<DlcoAnalysisSet>();
    const [session, setSession] = React.useState<DlcoSession>();
    const [sessionNotes, setSessionNotes] = React.useState<SessionNote[]>(null);
    const [isViewOnly, setIsViewOnly] = React.useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | undefined>();
    const [noteError, setNoteError] = React.useState<string | undefined>();
    const [studyRelevantParameters, setStudyRelevantParameters] = React.useState<StudyRelevantParameters>(null);
    const { currentUser, currentUserPermissions } = useSecurityContext();
    const { study } = useCurrentStudyConfigurationContext();

    const fetchSessionNotes = async (sessionId: string) => {
        const notes = await client.client.getSessionNotes(undefined, `sessionId eq ${sessionId}`);
        setSessionNotes(notes.value);
    }

    React.useEffect(() => {
        const fetchAnalysisSet = async () => {
            const access = await client.client.getDlcoAnalysisSet(analysisSetId, "canEdit,status,user");
            setIsViewOnly(!access.canEdit);
            const overreadPermission = currentUserPermissions.find(p => p.Id == PermissionEnum.OverreadDlcoSession);
            setIsViewOnly(!overreadPermission);

            const dlcoSettings = await legacyClient.getStudyDlcoSettings(study.id);
            setHbParameterDisplay(dlcoSettings.DisplayHbParameterInDlcoOverreadingScreen);

            if (access.canEdit && viewState === ViewState.Overread && overreadPermission) {
                if (dlcoSettings.DisableDlcoOverreadWithoutHB === true && access.status === OverreadStatusIdEnum.Pending && access.user === null) {
                    setIsViewOnly(true);

                } else {
                    setIsViewOnly(false);

                    await legacyClient.startOverreading(analysisSetId);
                }
            } else {
                setIsViewOnly(true);
            }

            const results = await legacyClient.getDlcoAnalysisSetResults(analysisSetId);
            setAnalysisSet(results);

            const sessionMetadataResponse = await legacyClient.getDlcoSession(results.SessionId);
            setSessionMetadata(sessionMetadataResponse);

            const currentAnalysisSet = sessionMetadataResponse.AnalysisSets.find(a => a.Id === analysisSetId);
            const isQc = currentAnalysisSet.Type.Id === DlcoAnalysisSetTypeEnum.QC;

            const overreadAnalysisSetId = viewState === ViewState.Overread || isQc ? analysisSetId : sessionMetadataResponse.AnalysisSets.find(a => a.Type.Id === DlcoAnalysisSetTypeEnum.Overread).Id;
            const overreadAnalysisSetResult = await client.client.getDlcoAnalysisSet(overreadAnalysisSetId, undefined, "tests");
            setOverreadAnalysisSet(overreadAnalysisSetResult);

            const sessionResult = await client.client.getDlcoSession(sessionMetadataResponse.Id, undefined, "tests");
            setSession(sessionResult);

            const parameters = await legacyClient.getStudyDlcoRelevantParameters();
            setStudyRelevantParameters(parameters);

            await fetchSessionNotes(results.SessionId);

            fixedCommentDispatchOverreading(LoadAnalysisSetSelectionsAction.create(overreadAnalysisSetResult.fixedComments, overreadAnalysisSetResult.tests));
        }

        fetchAnalysisSet();
    }, [analysisSetId, viewState]);

    const [shouldValidateFixedComments, setShouldValidateFixedComments] = React.useState(false);

    const [fixedCommentStateOverreading, fixedCommentDispatchOverreading] = useNextGenFixedComments(SessionTypes.Dlco, FixedCommentSetTypeEnum.Overread, overreadAnalysisSet);
    const [acceptabilityState, acceptabilityDispatch] = useAcceptability(analysisSet);

    React.useEffect(() => {

        const updateBestTestAndRepeatability = async () => {
            if (acceptabilityState.spirometryResults.TestResults.length) {

                acceptabilityDispatch(LoadingBestValuesAction.create());

                const dlcoRepeatabilityValues = await legacyClient.calculateDlcoRepeatability(acceptabilityState.spirometryResults.TestResults);
                const dlcoBestTestValues = await legacyClient.calculateDlcoBestTests(acceptabilityState.spirometryResults.TestResults);

                acceptabilityDispatch(UpdateBestTestAction.create(dlcoBestTestValues));
                acceptabilityDispatch(UpdateRepeatabilityAction.create(dlcoRepeatabilityValues));

                acceptabilityDispatch(DoneLoadingBestValuesAction.create());
            }
        }

        updateBestTestAndRepeatability();
    }, [acceptabilityState.changedAcceptability, acceptabilityDispatch]);

    if (analysisSet === null || sessionMetadata === null || sessionNotes === null) {
        return (<LoadingWithMessage message="Loading DLco Data..." />);
    }

    const setAnalysisDecision = async (annotation: string, decision: OverreadStatusEnum) => {
        setIsSubmitting(true);
        setError(undefined);

        const sessionFixedCommentsMutation = fixedCommentStateOverreading.sessionFixedComments.comments.map(x => ({ CommentId: x.id, IsSelected: x.selected } as FixedCommentMutation));
        const testFixedCommentsMutation = fixedCommentStateOverreading.testFixedComments.map(x => ({
            TestId: x.testId, Comments: x.comments.map(c => ({ CommentId: c.id, IsSelected: c.selected } as FixedCommentMutation)),
        }));
        const testAcceptability = acceptabilityState.spirometryResults.TestResults.map(x => ({ TestId: x.TestId, Accepted: x.OverreaderAcceptability ? true : false } as TestAcceptabilityMutation));

        try {
            const response = await legacyClient.updateAcceptabilityDlco(analysisSetId, decision, testAcceptability, sessionFixedCommentsMutation, testFixedCommentsMutation, annotation);

            if (response.AnalysisSetId !== analysisSetId) {
                setError("An error occurred saving the analysis set. Please contact an Administrator.");
            }

            GoBackToList(navigate, currentUserPermissions, "/Dlco", PermissionEnum.OverreadDlcoSession, "/Dlco/Analysis");
        }
        catch (ex) {
            setError(ex.Message);
            window.scrollTo(0, 0);
        }
    }

    const addNewNote = async (annotation: string) => {
        try {
            await legacyClient.putSessionNotes(analysisSet.SessionId, annotation);
        } catch (ex) {
            setNoteError(ex.Message);
            return;
        }
        await fetchSessionNotes(analysisSet.SessionId);
    }

    const editNote = async (id: string, comment: string, annotation: string) => {
        await client.client.patchSessionNote(
            {
                comment: comment,
            }, id, annotation);
        await fetchSessionNotes(analysisSet.SessionId);
    }

    const isOverread = viewState === "overread";
    const overreadingDisabled = isOverread && (fixedCommentStateOverreading.areAvailableFixedCommentsLoaded === false || (fixedCommentStateOverreading.areFixedCommentsLoaded === undefined || fixedCommentStateOverreading.areFixedCommentsLoaded === null || fixedCommentStateOverreading.areFixedCommentsLoaded === false));

    const hasAddingNotePermission = currentUserPermissions.some(x => x.Id === PermissionEnum.AddDlcoSessionNotes);
    const hasViewNotePermission = currentUserPermissions.some(x => x.Id === PermissionEnum.ViewDlcoSessionNotes);

    const pageAnalysisSet = sessionMetadata.AnalysisSets.find(x => x.Id === analysisSetId);
    const pageAnalysisSetType = pageAnalysisSet.Type.Id;
    const isQcAnalysisSet = pageAnalysisSetType === DlcoAnalysisSetTypeEnum.QC;
    const isSiteAnalysisSet = pageAnalysisSetType === DlcoAnalysisSetTypeEnum.Site;
    const isOverreadAnalysisSet = pageAnalysisSetType === DlcoAnalysisSetTypeEnum.Overread;
    const isUnauthorized = isQcAnalysisSet && pageAnalysisSet.Analyst.Username && pageAnalysisSet.Analyst.Username != currentUser.Username;

    if (isUnauthorized) {
        return <Navigate to={{ pathname: "/unauthorized" }} />;
    }

    if (isOverread && isSiteAnalysisSet) {
        return <Navigate to={{ pathname: `/Dlco/Analysis/${analysisSetId}/view` }} />;
    }

    return (
        <>
            <Alert show={overreadingDisabled} category={AlertCategoryEnum.Danger}>
                Over-reading disabled. Fixed comments have not been configured for this study, please ask the Administrator to configure these first.
            </Alert>

            <Alert show={error !== undefined} category={AlertCategoryEnum.Danger}>
                {error}
            </Alert>

            {!isQcAnalysisSet && !isSiteAnalysisSet && !isViewOnly && sessionMetadata.ChallengeStatusId === OverreadChallengeStatusEnum.Challenged && !sessionMetadata.ChallengeIsClosed ?
                <ChallengeAlert
                    sessionId={sessionMetadata.Id}
                    sessionType={SessionTypes.Dlco}
                    onRejectComplete={() => GoBackToList(navigate, currentUserPermissions, "/Dlco", PermissionEnum.OverreadDlcoSession, "/Dlco/Analysis")}
                /> : null}

            <h2 className="text-primary">DLco Data</h2>
            {!isQcAnalysisSet ? <SessionMetadataTable
                sessionMetadata={sessionMetadata}
                selectedAnalysisSetId={analysisSetId}
                HbParameterDisplay={hbParameterDisplay}
            /> : <Alert show={true} category={AlertCategoryEnum.Info}>Session Information is hidden from view in this workflow</Alert>}

            <h2 className="text-primary">Results</h2>
            <div className="panel panel-default">
                <div className="panel-body">
                    <DlcoResultsTable
                        isViewOnly={isViewOnly || overreadingDisabled || acceptabilityState.loadingBestValues}
                        isSiteAnalysisSet={isSiteAnalysisSet}
                        resultData={acceptabilityState.spirometryResults}
                        session={session}
                        fixedCommentsStateOverreading={fixedCommentStateOverreading}
                        fixedCommentsDispatchOverreading={fixedCommentDispatchOverreading}
                        shouldValidateFixedComments={shouldValidateFixedComments}
                        acceptabilityState={acceptabilityState}
                        acceptabilityDispatch={acceptabilityDispatch}
                        studyDlcoRelevantParameters={studyRelevantParameters}
                    />
                </div>
            </div>
            <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.AccessDlcoAnalysisSetsAttachment}>
                <h2 data-automationid="Attachments" className="text-primary">Attachments</h2>
                <div className="panel panel-default">
                    <div className="panel-body">
                        <FileAttachmentsTable
                            legacyClient={legacyClient}
                            fileAttachments={analysisSet.Attachments.map(a => ({ id: a.Id, fileSizeInBytes: a.FileSizeInBytes, fileName: a.FileName }))}
                            mode={FileAttachmentsTableMode.ListOnly}
                        />
                    </div>
                </div>
            </Authorization>
            {
                hasViewNotePermission ?
                    <NotesPanel
                        buttonDisabled={sessionMetadata.AnalysisSets.find(a => a.Id === analysisSetId).Type.Id === DlcoAnalysisSetTypeEnum.QC}
                        header="Session Notes"
                        showNotesCount={true}
                        isAddingNotesEnabled={!isViewOnly && !overreadingDisabled && hasAddingNotePermission && isOverreadAnalysisSet}
                        notes={sessionNotes}
                        errorStatus={noteError}
                        modalSize={ModalSize.Large}
                        onEditNote={(id, comment, annotation) => editNote(id, comment, annotation)}
                        onAddNote={(annotation) => addNewNote(annotation)} />
                    : null
            }
            <AcceptabilityDashboard
                isViewOnly={isViewOnly || overreadingDisabled}
                analysisSetId={analysisSetId}
                fixedCommentsDispatch={fixedCommentDispatchOverreading}
                fixedCommentState={fixedCommentStateOverreading}
                acceptabilityState={acceptabilityState}
                sessionType={SessionTypes.Dlco}
                overreadStatus={sessionMetadata.AnalysisSets.find(a => a.Id === analysisSetId).Status.Id}
                onNoSessionCommentsSelected={() => {
                    fixedCommentDispatchOverreading(ToggleSessionFixedCommentsVisibilityAction.create());
                }}
                onAcceptabilitySelected={(annotation, decision) => setAnalysisDecision(annotation, decision)}
                onCancel={() => GoBackToList(navigate, currentUserPermissions, "/Dlco", PermissionEnum.OverreadDlcoSession, "/Dlco/Analysis")}
            />
        </>
    );
};
